import { Fragment, type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'
import {
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  Search,
} from 'lucide-react'
import {
  useGetPackagingShipmentResult,
  type PackagingShipmentResultRow,
  type PackagingShipmentResultStatus,
} from '@/shared/hooks/api/packagingShipments'
import { useSearchParamsNavigate } from '@/shared/hooks/use-search-params-navigate'
import { useTableUrlState } from '@/shared/hooks/use-table-url-state'
import { DataTablePagination } from '@/components/data-table'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { SegmentedProgressBar } from './SegmentedProgressBar'
import { ShipmentEprPanel } from './ShipmentEprPanel'
import {
  groupShipmentResultRowsBySku,
  type ShipmentResultCheckSummary,
  type ShipmentResultSkuSummary,
} from './shipmentResultSkuGroups'
import type { TFunction } from 'i18next'

type ShipmentCheckResultProps = {
  shipmentId: string
  displayedCheckId: string | null
  skusUpdatedAt: string
  headerAction?: ReactNode
}

function SkusSectionHeader({
  title,
  action,
  aside,
}: {
  title: string
  action?: ReactNode
  aside?: ReactNode
}) {
  return (
    <div className='flex shrink-0 flex-wrap items-center justify-between gap-2'>
      <h3 className='text-lg font-semibold'>{title}</h3>
      <div className='flex flex-wrap items-center gap-2'>
        {aside}
        {action}
      </div>
    </div>
  )
}

const RESULT_STATUSES = [
  'attention',
  'notInLex',
  'noPackaging',
  'declarationMissing',
] as const

function parseStatus(value: unknown): PackagingShipmentResultStatus | undefined {
  if (typeof value !== 'string') {
    return undefined
  }
  return RESULT_STATUSES.find((status) => status === value)
}

function resultStatusFilterLabel(
  translate: TFunction<'packagingSuppliers'>,
  value: string
): string {
  if (value === 'all') {
    return translate('shipmentsPage.result.filterAll')
  }
  if (value === 'attention') {
    return translate('shipmentsPage.result.filterAttention')
  }
  if (value === 'notInLex') {
    return translate('shipmentsPage.result.filterNotInLex')
  }
  if (value === 'noPackaging') {
    return translate('shipmentsPage.result.filterNoPackaging')
  }
  if (value === 'declarationMissing') {
    return translate('shipmentsPage.result.filterDeclarationMissing')
  }
  return translate('shipmentsPage.result.filterStatus')
}

function labelText(
  translate: TFunction<'packagingSuppliers'>,
  value: string
): string {
  if (value === 'Clear') {
    return translate('shipmentsPage.result.clear')
  }
  if (value === 'Requires Attention') {
    return translate('shipmentsPage.result.requiresAttention')
  }
  if (value === 'Not run') {
    return translate('shipmentsPage.result.notRun')
  }
  if (value.length === 0) {
    return ''
  }
  return value
}

function dataCheckDetailText(
  translate: TFunction<'packagingSuppliers'>,
  detail: string
): string {
  if (detail.length === 0) {
    return ''
  }
  return detail
    .split(', ')
    .map((code) => translate(`fieldCrossCheck.checks.${code}`))
    .join(', ')
}

function auditVerdictDetailText(
  translate: TFunction<'packagingSuppliers'>,
  prefix:
    | 'itemDetail.heavyMetalsAudit.verdicts'
    | 'itemDetail.svhcAudit.verdicts',
  detail: string
): string {
  if (detail.length === 0) {
    return ''
  }
  return translate(`${prefix}.${detail}`)
}

function checkTooltip(
  translate: TFunction<'packagingSuppliers'>,
  status: string,
  detail: string
): string {
  const label = labelText(translate, status)
  if (label.length === 0) {
    return ''
  }
  return detail.length > 0 ? `${label} — ${detail}` : label
}

function CheckStatusIcon({
  status,
  tooltip,
}: {
  status: string
  tooltip: string
}) {
  let icon = (
    <CircleDashed className='size-4 text-muted-foreground' aria-hidden />
  )
  if (status === 'Clear') {
    icon = (
      <CircleCheck
        className='size-4 text-green-600 dark:text-green-500'
        aria-hidden
      />
    )
  } else if (status === 'Requires Attention') {
    icon = <CircleAlert className='size-4 text-destructive' aria-hidden />
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type='button'
          className='inline-flex rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring'
          aria-label={tooltip}
        >
          {icon}
        </button>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  )
}

function PackedItemLink({ id, name }: { id: string; name: string }) {
  return (
    <Link
      className='text-foreground underline decoration-dotted underline-offset-2'
      to={`/packaging/packed-items/${id}`}
    >
      {name}
    </Link>
  )
}

function declarationLineStatus(generated: boolean): string {
  return generated ? 'Clear' : 'Not run'
}

function ResultLineCells({
  row,
  translate,
}: {
  row: PackagingShipmentResultRow
  translate: TFunction<'packagingSuppliers'>
}) {
  return (
    <>
      <TableCell>{row.packagingItemName}</TableCell>
      <TableCell>
        {row.type === 'PACKAGING_ITEM' ? (
          <CheckStatusIcon
            status={declarationLineStatus(row.declarationGenerated)}
            tooltip={
              row.declarationGenerated
                ? translate('shipmentsPage.result.yes')
                : translate('shipmentsPage.result.no')
            }
          />
        ) : null}
      </TableCell>
      <TableCell>
        <CheckStatusIcon
          status={row.dataCheckLabel}
          tooltip={checkTooltip(
            translate,
            row.dataCheckLabel,
            dataCheckDetailText(translate, row.dataCheckDetail)
          )}
        />
      </TableCell>
      <TableCell>
        <CheckStatusIcon
          status={row.heavyMetalsLabel}
          tooltip={checkTooltip(
            translate,
            row.heavyMetalsLabel,
            auditVerdictDetailText(
              translate,
              'itemDetail.heavyMetalsAudit.verdicts',
              row.heavyMetalsDetail
            )
          )}
        />
      </TableCell>
      <TableCell>
        <CheckStatusIcon
          status={row.svhcLabel}
          tooltip={checkTooltip(
            translate,
            row.svhcLabel,
            auditVerdictDetailText(
              translate,
              'itemDetail.svhcAudit.verdicts',
              row.svhcDetail
            )
          )}
        />
      </TableCell>
    </>
  )
}

function SummaryCheckIcon({
  check,
  translate,
}: {
  check: ShipmentResultCheckSummary
  translate: TFunction<'packagingSuppliers'>
}) {
  const tooltip =
    check.attention + check.notRun + check.clear === 0
      ? ''
      : translate('shipmentsPage.result.summary.checkCounts', {
          attention: check.attention,
          notRun: check.notRun,
          clear: check.clear,
        })
  return <CheckStatusIcon status={check.worst} tooltip={tooltip} />
}

function SummaryCells({
  summary,
  translate,
}: {
  summary: ShipmentResultSkuSummary
  translate: TFunction<'packagingSuppliers'>
}) {
  return (
    <>
      <TableCell>
        {summary.packedItem != null ? (
          <PackedItemLink
            id={summary.packedItem.id}
            name={summary.packedItem.name}
          />
        ) : summary.packedItemCount > 1 ? (
          translate('shipmentsPage.result.summary.packedItems', {
            count: summary.packedItemCount,
          })
        ) : null}
      </TableCell>
      <TableCell>
        {summary.supplierName != null
          ? summary.supplierName
          : summary.supplierCount > 1
            ? translate('shipmentsPage.result.summary.suppliers', {
                count: summary.supplierCount,
              })
            : null}
      </TableCell>
      <TableCell>
        {summary.packagingCount > 0
          ? translate('shipmentsPage.result.summary.packagingItems', {
              count: summary.packagingCount,
            })
          : null}
      </TableCell>
      <TableCell>
        {summary.packagingCount > 0
          ? translate('shipmentsPage.result.summary.declarations', {
              ready: summary.declarationsReady,
              total: summary.packagingCount,
            })
          : null}
      </TableCell>
      <TableCell>
        <SummaryCheckIcon check={summary.dataCheck} translate={translate} />
      </TableCell>
      <TableCell>
        <SummaryCheckIcon check={summary.heavyMetals} translate={translate} />
      </TableCell>
      <TableCell>
        <SummaryCheckIcon check={summary.svhc} translate={translate} />
      </TableCell>
    </>
  )
}

export function ShipmentCheckResult({
  shipmentId,
  displayedCheckId,
  skusUpdatedAt,
  headerAction,
}: ShipmentCheckResultProps) {
  const { t } = useTranslation('packagingSuppliers')
  const { search, navigate } = useSearchParamsNavigate()
  const { pagination, onPaginationChange, ensurePageInRange } =
    useTableUrlState({
      search,
      navigate,
      pagination: { defaultPage: 1, defaultPageSize: 20 },
    })
  const page = pagination.pageIndex + 1
  const pageSize = Math.min(Math.max(pagination.pageSize, 1), 50)
  const status = parseStatus(search.status)
  const urlQuery = typeof search.q === 'string' ? search.q : ''
  const [searchInput, setSearchInput] = useState(urlQuery)
  const [expandMode, setExpandMode] = useState<'collapsed' | 'expanded'>(
    status != null || urlQuery.length > 0 ? 'expanded' : 'collapsed'
  )
  const [toggledSkus, setToggledSkus] = useState<Set<string>>(() => new Set())
  const previousCheckId = useRef(displayedCheckId)
  const filterKey = `${status ?? ''}\n${urlQuery}`
  const previousFilterKey = useRef(filterKey)
  const pageKey = `${displayedCheckId ?? ''}:${page}`
  const previousPageKey = useRef(pageKey)

  useEffect(() => {
    if (previousFilterKey.current === filterKey) {
      return
    }
    previousFilterKey.current = filterKey
    setExpandMode(status != null || urlQuery.length > 0 ? 'expanded' : 'collapsed')
    setToggledSkus(new Set())
  }, [filterKey, status, urlQuery])

  useEffect(() => {
    if (previousPageKey.current === pageKey) {
      return
    }
    previousPageKey.current = pageKey
    setToggledSkus(new Set())
  }, [pageKey])

  useEffect(() => {
    setSearchInput(urlQuery)
  }, [urlQuery])

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = searchInput.trim()
      if (next === urlQuery) {
        return
      }
      navigate({
        search: (prev) => ({
          ...prev,
          q: next.length > 0 ? next : undefined,
          page: undefined,
        }),
      })
    }, 300)
    return () => {
      window.clearTimeout(handle)
    }
  }, [navigate, searchInput, urlQuery])

  useEffect(() => {
    if (previousCheckId.current === displayedCheckId) {
      return
    }
    previousCheckId.current = displayedCheckId
    navigate({
      replace: true,
      search: (prev) => ({
        ...prev,
        page: undefined,
      }),
    })
  }, [displayedCheckId, navigate])

  const result = useGetPackagingShipmentResult(
    shipmentId,
    displayedCheckId,
    page,
    pageSize,
    { status, skuQuery: urlQuery.length > 0 ? urlQuery : undefined },
    displayedCheckId != null
  )

  const pageInfo = result.data?.pageInfo
  useEffect(() => {
    if (pageInfo == null) {
      return
    }
    ensurePageInRange(pageInfo.totalPages)
  }, [ensurePageInRange, pageInfo])

  const columns = useMemo(() => [{ id: 'sku' }], [])
  const skuGroups = useMemo(
    () => groupShipmentResultRowsBySku(result.data?.rows ?? []),
    [result.data?.rows]
  )
  const hasMultiLineSku = skuGroups.some((group) => group.rows.length > 1)
  const skuIsExpanded = (sku: string) =>
    expandMode === 'expanded'
      ? !toggledSkus.has(sku)
      : toggledSkus.has(sku)
  const toggleSku = (sku: string) => {
    setToggledSkus((current) => {
      const next = new Set(current)
      if (next.has(sku)) {
        next.delete(sku)
      } else {
        next.add(sku)
      }
      return next
    })
  }
  const table = useReactTable({
    data: result.data?.rows ?? [],
    columns,
    state: { pagination: { pageIndex: pagination.pageIndex, pageSize } },
    onPaginationChange,
    manualPagination: true,
    pageCount: pageInfo?.totalPages ?? 0,
    rowCount: pageInfo?.totalCount ?? 0,
    getCoreRowModel: getCoreRowModel(),
  })

  const setStatus = (next: PackagingShipmentResultStatus | undefined) => {
    navigate({
      search: (prev) => ({
        ...prev,
        status: next,
        page: undefined,
      }),
    })
  }

  if (displayedCheckId == null) {
    return (
      <section className='flex flex-col gap-3'>
        <SkusSectionHeader
          title={t('shipmentsPage.result.skusSection')}
          action={headerAction}
        />
        <div className='flex items-center justify-center rounded-md border border-dashed py-16 text-center text-sm text-muted-foreground'>
          {t('shipmentsPage.detail.availableAfterFirstCheck')}
        </div>
      </section>
    )
  }

  if (result.data == null || pageInfo == null) {
    return (
      <section className='flex flex-col gap-3'>
        <SkusSectionHeader
          title={t('shipmentsPage.result.skusSection')}
          action={headerAction}
        />
        <p className='text-sm text-muted-foreground'>
          {t('shipmentsPage.loading')}
        </p>
      </section>
    )
  }

  const data = result.data
  const stale =
    data.finishedAt != null &&
    new Date(data.finishedAt).getTime() < new Date(skusUpdatedAt).getTime()
  const packagingTotal =
    data.packagingItemsClearCount + data.packagingItemsRequiringAttentionCount
  const filtered =
    status != null || urlQuery.length > 0

  return (
    <section className='flex flex-col gap-4'>
      <SkusSectionHeader
        title={t('shipmentsPage.result.skusSection')}
        action={headerAction}
        aside={
          stale ? (
            <p className='text-sm text-muted-foreground'>
              {t('shipmentsPage.result.staleResult')}
            </p>
          ) : null
        }
      />

      {packagingTotal > 0 && (
        <div className='shrink-0'>
          <SegmentedProgressBar
            total={packagingTotal}
            segments={[
              {
                key: 'attention',
                count: data.packagingItemsRequiringAttentionCount,
                className: 'bg-destructive',
                label: t('shipmentsPage.result.progressAttention'),
                tooltip: t('shipmentsPage.result.progressAttentionTooltip'),
                onSelect: () => {
                  setStatus(status === 'attention' ? undefined : 'attention')
                },
              },
              {
                key: 'clear',
                count: data.packagingItemsClearCount,
                className: 'bg-green-600 dark:bg-green-500',
                label: t('shipmentsPage.result.progressClear'),
                tooltip: t('shipmentsPage.result.progressClearTooltip'),
                onSelect: () => {
                  setStatus(undefined)
                },
              },
            ]}
          />
        </div>
      )}

      <Tabs defaultValue='compliance' className='gap-4'>
        <TabsList>
          <TabsTrigger value='compliance'>
            {t('shipmentsPage.result.epr.complianceTab')}
          </TabsTrigger>
          <TabsTrigger value='epr'>
            {t('shipmentsPage.result.epr.tab')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value='compliance' className='flex flex-col gap-4'>
      <dl className='grid shrink-0 grid-cols-2 gap-3 sm:grid-cols-4'>
        {(
          [
            ['skuCount', data.skuCount],
            ['notInLex', data.notInLexCount],
            ['packedItems', data.packedItemCount],
            ['declarationsReady', data.declarationsReadyCount],
          ] as const
        ).map(([key, value]) => (
          <div key={key}>
            <dt className='text-sm text-muted-foreground'>
              {t(`shipmentsPage.result.counts.${key}`)}
            </dt>
            <dd className='text-lg font-semibold'>{value}</dd>
          </div>
        ))}
      </dl>

      {data.notInLexCount > 0 && (
        <Alert variant='warning' className='shrink-0'>
          <AlertDescription className='flex flex-wrap items-center justify-between gap-2'>
            <span>
              {t('shipmentsPage.result.notInLexAlert', {
                count: data.notInLexCount,
              })}
            </span>
            <Button
              type='button'
              size='sm'
              variant='outline'
              onClick={() => setStatus('notInLex')}
            >
              {t('shipmentsPage.result.showNotInLex')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <div className='flex shrink-0 flex-wrap items-center gap-2'>
        <InputGroup className='h-8 w-full max-w-64'>
          <InputGroupAddon>
            <Search aria-hidden />
          </InputGroupAddon>
          <InputGroupInput
            value={searchInput}
            placeholder={t('shipmentsPage.result.searchPlaceholder')}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </InputGroup>
        <Select
          value={status ?? 'all'}
          onValueChange={(value) => {
            setStatus(value === 'all' || value == null ? undefined : parseStatus(value))
          }}
        >
          <SelectTrigger className='h-8 w-52'>
            <SelectValue placeholder={t('shipmentsPage.result.filterStatus')}>
              {resultStatusFilterLabel(t, status ?? 'all')}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>
              {t('shipmentsPage.result.filterAll')}
            </SelectItem>
            <SelectItem value='attention'>
              {t('shipmentsPage.result.filterAttention')}
            </SelectItem>
            <SelectItem value='notInLex'>
              {t('shipmentsPage.result.filterNotInLex')}
            </SelectItem>
            <SelectItem value='noPackaging'>
              {t('shipmentsPage.result.filterNoPackaging')}
            </SelectItem>
            <SelectItem value='declarationMissing'>
              {t('shipmentsPage.result.filterDeclarationMissing')}
            </SelectItem>
          </SelectContent>
        </Select>
        {hasMultiLineSku && (
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='ms-auto'
            onClick={() => {
              setExpandMode(expandMode === 'expanded' ? 'collapsed' : 'expanded')
              setToggledSkus(new Set())
            }}
          >
            {expandMode === 'expanded' ? (
              <ChevronsDownUp aria-hidden />
            ) : (
              <ChevronsUpDown aria-hidden />
            )}
            {expandMode === 'expanded'
              ? t('shipmentsPage.result.collapseAll')
              : t('shipmentsPage.result.expandAll')}
          </Button>
        )}
      </div>

      <div
        className='flex h-[32rem] flex-col overflow-hidden rounded-md border'
        aria-busy={result.isFetching}
      >
        <div className='min-h-0 flex-1 overflow-auto'>
          <table className='w-full caption-bottom text-sm'>
            <TableHeader>
              <TableRow>
                <TableHead className='sticky top-0 left-0 z-20 bg-background'>
                  {t('shipmentsPage.result.columns.sku')}
                </TableHead>
                <TableHead className='sticky top-0 z-10 bg-background'>
                  {t('shipmentsPage.result.columns.packedItem')}
                </TableHead>
                <TableHead className='sticky top-0 z-10 bg-background'>
                  {t('shipmentsPage.result.columns.supplier')}
                </TableHead>
                <TableHead className='sticky top-0 z-10 bg-background'>
                  {t('shipmentsPage.result.columns.packagingItem')}
                </TableHead>
                <TableHead className='sticky top-0 z-10 bg-background'>
                  {t('shipmentsPage.result.columns.declaration')}
                </TableHead>
                <TableHead className='sticky top-0 z-10 bg-background'>
                  {t('shipmentsPage.result.columns.dataCheck')}
                </TableHead>
                <TableHead className='sticky top-0 z-10 bg-background'>
                  {t('shipmentsPage.result.columns.heavyMetals')}
                </TableHead>
                <TableHead className='sticky top-0 z-10 bg-background'>
                  {t('shipmentsPage.result.columns.svhc')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className='h-24 text-center text-muted-foreground'
                  >
                    {filtered
                      ? t('shipmentsPage.result.noFilterMatches')
                      : t('shipmentsPage.detail.availableAfterFirstCheck')}
                  </TableCell>
                </TableRow>
              )}
              {skuGroups.map((group) => {
                const single = group.rows[0]
                if (group.rows.length === 1 && single != null) {
                  return (
                    <TableRow key={group.sku}>
                      <TableCell className='sticky left-0 z-10 bg-background font-medium'>
                        <span className='block max-w-40 truncate' title={group.sku}>
                          {group.sku}
                        </span>
                      </TableCell>
                      <TableCell>
                        {single.packedItemId != null &&
                        single.packedItemName != null ? (
                          <PackedItemLink
                            id={single.packedItemId}
                            name={single.packedItemName}
                          />
                        ) : null}
                      </TableCell>
                      <TableCell>{single.supplierName}</TableCell>
                      <ResultLineCells row={single} translate={t} />
                    </TableRow>
                  )
                }
                const expanded = skuIsExpanded(group.sku)
                return (
                  <Fragment key={group.sku}>
                    <TableRow>
                      <TableCell className='sticky left-0 z-10 bg-background font-medium'>
                        <span className='flex items-center gap-1'>
                          <button
                            type='button'
                            className='inline-flex size-6 shrink-0 items-center justify-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring'
                            aria-expanded={expanded}
                            aria-label={
                              expanded
                                ? t('shipmentsPage.result.collapseSku', {
                                    sku: group.sku,
                                  })
                                : t('shipmentsPage.result.expandSku', {
                                    sku: group.sku,
                                  })
                            }
                            onClick={() => toggleSku(group.sku)}
                          >
                            {expanded ? (
                              <ChevronDown className='size-4' aria-hidden />
                            ) : (
                              <ChevronRight className='size-4' aria-hidden />
                            )}
                          </button>
                          <span className='block max-w-40 truncate' title={group.sku}>
                            {group.sku}
                          </span>
                        </span>
                      </TableCell>
                      <SummaryCells summary={group.summary} translate={t} />
                    </TableRow>
                    {expanded &&
                      group.rows.map((row, index) => {
                        const showPacked =
                          index === 0 ||
                          group.rows[index - 1]?.packedItemId !== row.packedItemId
                        return (
                          <TableRow
                            key={`${row.sku}-${row.packedItemId ?? 'none'}-${row.packagingItemId ?? index}`}
                            className='bg-muted/30'
                          >
                            <TableCell className='sticky left-0 z-10 bg-muted' />
                            <TableCell>
                              {showPacked &&
                              row.packedItemId != null &&
                              row.packedItemName != null ? (
                                <PackedItemLink
                                  id={row.packedItemId}
                                  name={row.packedItemName}
                                />
                              ) : null}
                            </TableCell>
                            <TableCell>
                              {showPacked ? row.supplierName : null}
                            </TableCell>
                            <ResultLineCells row={row} translate={t} />
                          </TableRow>
                        )
                      })}
                  </Fragment>
                )
              })}
            </TableBody>
          </table>
        </div>
        <DataTablePagination
          table={table}
          className='shrink-0 border-t bg-background py-3'
        />
      </div>
        </TabsContent>
        <TabsContent value='epr' className='flex flex-col gap-4'>
          <ShipmentEprPanel shipmentId={shipmentId} />
        </TabsContent>
      </Tabs>
    </section>
  )
}
